import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, History, CalendarClock, Eye, EyeOff } from "lucide-react";

const selectCls =
  "w-full bg-black border-2 border-[#E3BC55]/70 rounded-2xl px-4 py-3.5 text-base text-slate-200 focus:outline-none focus:border-[#FFD700] transition-colors appearance-none";
const inputCls =
  "w-full bg-black border-2 border-[#E3BC55]/50 rounded-2xl px-3 py-3 font-mono-data text-center text-lg text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#FFD700] transition-colors";

function Pill({ children }) {
  return (
    <span className="block bg-[#2A2A2A] text-white text-sm font-semibold text-center rounded-md px-3 py-1.5 tracking-wide">
      {children}
    </span>
  );
}

function GoldHeading({ children }) {
  return (
    <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#E3BC55] uppercase tracking-[0.18em] text-center">
      {children}
    </h2>
  );
}

export default function CalcoloPage({ onAnalysis }) {
  const [leagues, setLeagues] = useState([]);
  const [league, setLeague] = useState("");
  const [teams, setTeams] = useState([]);
  const [home, setHome] = useState("");
  const [away, setAway] = useState("");
  const [odds, setOdds] = useState({});
  const [h2h, setH2h] = useState(null);
  const [h2hLoading, setH2hLoading] = useState(false);
  const [showH2h, setShowH2h] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/football/leagues").then((r) => setLeagues(r.data)).catch(() => toast.error("Errore caricamento campionati"));
  }, []);

  useEffect(() => {
    setTeams([]);
    setHome("");
    setAway("");
    setH2h(null);
    if (!league) return;
    api.get(`/football/leagues/${league}/teams`).then((r) => setTeams(r.data.teams));
  }, [league]);

  useEffect(() => {
    setH2h(null);
    setShowH2h(false);
    if (!home || !away || home === away) return;
    setH2hLoading(true);
    api
      .get("/football/h2h", { params: { home, away, league } })
      .then((r) => setH2h(r.data))
      .catch(() => toast.error("Errore recupero H2H"))
      .finally(() => setH2hLoading(false));
  }, [home, away]);

  const ODDS_KEYS = ["odds_1", "odds_x", "odds_2", "odds_over_15", "odds_under_15", "odds_over_25", "odds_under_25", "odds_over_35", "odds_under_35"];
  const oddsComplete = ODDS_KEYS.every((k) => parseFloat(odds[k]) > 1);
  const ready = league && home && away && home !== away && oddsComplete;

  const setOdd = (k) => (e) => setOdds((o) => ({ ...o, [k]: e.target.value }));

  const elabora = async () => {
    setBusy(true);
    try {
      const leagueName = leagues.find((l) => l.id === league)?.name || league;
      const payload = { league: leagueName, league_id: league, home_team: home, away_team: away };
      ODDS_KEYS.forEach((k) => (payload[k] = parseFloat(odds[k])));
      const r = await api.post("/analyze", payload);
      toast.success("Dati elaborati!");
      onAnalysis(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore elaborazione");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-up max-w-xl mx-auto space-y-8">
      <GoldHeading>Seleziona il Match</GoldHeading>

      <div className="space-y-4">
        <div className="space-y-2">
          <Pill>CAMPIONATO</Pill>
          <select data-testid="calcolo-league-select" value={league} onChange={(e) => setLeague(e.target.value)} className={selectCls}>
            <option value="">Seleziona Campionato...</option>
            {leagues.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Pill>CASA</Pill>
            <select data-testid="calcolo-home-team-select" value={home} onChange={(e) => setHome(e.target.value)} disabled={!teams.length} className={selectCls}>
              <option value="">Seleziona Squadra...</option>
              {teams.filter((t) => t !== away).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Pill>TRASFERTA</Pill>
            <select data-testid="calcolo-away-team-select" value={away} onChange={(e) => setAway(e.target.value)} disabled={!teams.length} className={selectCls}>
              <option value="">Seleziona Squadra...</option>
              {teams.filter((t) => t !== home).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {(home && away && home !== away) && (
        <button
          data-testid="h2h-toggle-button"
          onClick={() => setShowH2h((s) => !s)}
          className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 bg-black text-[#E3BC55] font-heading font-bold uppercase text-sm tracking-[0.15em] transition-colors hover:text-[#FFD700] ${
            showH2h ? "border-[#E3BC55]" : "border-[#E3BC55]/60 blink-gold"
          }`}
        >
          {showH2h ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          {showH2h ? "Nascondi Head-to-Head" : "Mostra Head-to-Head"}
        </button>
      )}

      {(home && away && showH2h) && (
        <section data-testid="h2h-block" className="fade-up border-2 border-[#E3BC55]/60 rounded-2xl p-5 bg-black">
          <h3 className="font-mono-data text-xs text-[#E3BC55] tracking-widest uppercase mb-3 flex items-center gap-2">
            <History className="w-3.5 h-3.5" /> Head-to-Head Storico
            {h2h && (
              <span
                data-testid="h2h-source-badge"
                className={`ml-auto text-[9px] border rounded-full px-2 py-0.5 ${
                  h2h.source === "live" ? "text-emerald-400 border-emerald-500/40" : "text-slate-500 border-slate-600/50"
                }`}
              >
                {h2h.source === "live" ? "● LIVE" : "SIMULATO"}
              </span>
            )}
          </h3>
          {h2hLoading ? (
            <div className="flex items-center gap-2 text-[#E3BC55]/70 font-mono-data text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> RECUPERO DATI H2H…
            </div>
          ) : h2h ? (
            <div className="space-y-3">
              {h2h.next_match && h2h.next_match.utc_date && (
                <div data-testid="h2h-next-match" className="flex items-center gap-2.5 bg-[#E3BC55]/10 border border-[#E3BC55]/40 rounded-xl px-3 py-2.5">
                  <CalendarClock className="w-4 h-4 text-[#E3BC55] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] text-[#E3BC55]/80 uppercase tracking-widest font-mono-data">Match Effettivo</p>
                    <p className="text-sm text-white font-semibold capitalize">
                      {new Date(h2h.next_match.utc_date).toLocaleString("it-IT", {
                        weekday: "long", day: "2-digit", month: "long",
                        hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome",
                      })}
                    </p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-black border border-amber-500/10 rounded-xl py-2">
                  <p className="font-mono-data text-lg text-emerald-400">{h2h.stats.home_wins}</p>
                  <p className="text-[10px] text-slate-500 uppercase">Vitt. Casa</p>
                </div>
                <div className="bg-black border border-amber-500/10 rounded-xl py-2">
                  <p className="font-mono-data text-lg text-[#E3BC55]">{h2h.stats.draws}</p>
                  <p className="text-[10px] text-slate-500 uppercase">Pareggi</p>
                </div>
                <div className="bg-black border border-amber-500/10 rounded-xl py-2">
                  <p className="font-mono-data text-lg text-sky-400">{h2h.stats.away_wins}</p>
                  <p className="text-[10px] text-slate-500 uppercase">Vitt. Trasf.</p>
                </div>
              </div>
              <div className="flex justify-between font-mono-data text-xs text-slate-400">
                <span>Media gol: <span className="text-[#E3BC55]">{h2h.stats.avg_goals}</span></span>
                <span>Over 2.5: <span className="text-[#E3BC55]">{h2h.stats.over_25_pct}%</span></span>
              </div>
              <div className="space-y-1.5">
                {h2h.matches.slice(0, 5).map((m, i) => (
                  <div key={i} className="flex items-center justify-between bg-black border border-amber-500/10 rounded-lg px-3 py-1.5 text-xs">
                    <span className="font-mono-data text-slate-600">{m.date}</span>
                    <span className="text-slate-300 truncate mx-2 flex-1 text-center">{m.home} – {m.away}</span>
                    <span className="font-mono-data text-[#E3BC55]">{m.home_goals}-{m.away_goals}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </section>
      )}

      <div className="border-t border-slate-700/60" />

      <GoldHeading>Inserisci le Quote</GoldHeading>

      <div className="space-y-5">
        <div className="grid grid-cols-3 gap-4">
          {[
            { k: "odds_1", label: "1", testId: "calcolo-odds-1-input" },
            { k: "odds_x", label: "X", testId: "calcolo-odds-x-input" },
            { k: "odds_2", label: "2", testId: "calcolo-odds-2-input" },
          ].map((f) => (
            <div key={f.k} className="space-y-2">
              <span className="block bg-[#2A2A2A] text-white text-sm font-semibold text-center rounded-md px-3 py-1 w-16 mx-auto">{f.label}</span>
              <input data-testid={f.testId} type="number" step="0.01" min="1.01" placeholder="Quota" value={odds[f.k] || ""} onChange={setOdd(f.k)} className={inputCls} />
            </div>
          ))}
        </div>

        {[
          { line: "1.5", over: "odds_over_15", under: "odds_under_15", overId: "calcolo-odds-over15-input", underId: "calcolo-odds-under15-input" },
          { line: "2.5", over: "odds_over_25", under: "odds_under_25", overId: "calcolo-odds-over25-input", underId: "calcolo-odds-under25-input" },
          { line: "3.5", over: "odds_over_35", under: "odds_under_35", overId: "calcolo-odds-over35-input", underId: "calcolo-odds-under35-input" },
        ].map((row) => (
          <div key={row.line} className="grid grid-cols-[64px_1fr_1fr] gap-4 items-center">
            <span className="bg-[#2A2A2A] text-white text-sm font-semibold text-center rounded-md px-2 py-2">{row.line}</span>
            <input data-testid={row.overId} type="number" step="0.01" min="1.01" placeholder="Over" value={odds[row.over] || ""} onChange={setOdd(row.over)} className={inputCls} />
            <input data-testid={row.underId} type="number" step="0.01" min="1.01" placeholder="Under" value={odds[row.under] || ""} onChange={setOdd(row.under)} className={inputCls} />
          </div>
        ))}
      </div>

      <button
        data-testid="calcolo-elabora-dati-button"
        disabled={!ready || busy}
        onClick={elabora}
        className="relative overflow-hidden w-full py-4 rounded-xl bg-[#E3BC55] text-black font-heading font-bold text-xl uppercase tracking-[0.15em] disabled:opacity-35 hover:bg-[#FFD700] transition-colors flex items-center justify-center gap-2"
      >
        {busy && <span className="absolute inset-y-0 w-1/4 bg-white/30 scan-bar" />}
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
        {busy ? "Elaborazione…" : "Elabora Dati"}
      </button>
    </div>
  );
}
